import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Returning from Google OAuth: let AuthCallback exchange the session_id first.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        refresh();
    }, [refresh]);

    // Exchange an Emergent Google session_id for our app session, then load the user.
    const googleSession = async (sessionId) => {
        try {
            const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sessionId } });
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    // Exchange a Google authorization code (own OAuth client) for our app session.
    const googleOAuthCallback = async (code, redirectUri) => {
        try {
            const { data } = await api.post("/auth/google-oauth/callback", { code, redirect_uri: redirectUri });
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const register = async (name, email, password, username, profiles = [], pro_title = null, turnstile_token = null) => {
        try {
            const { data } = await api.post("/auth/register", { name, email, password, username, profiles, pro_title, turnstile_token });
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const registerPlayer = async (payload) => {
        try {
            const { data } = await api.post("/auth/register/player", payload);
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const registerTeam = async (payload) => {
        try {
            const { data } = await api.post("/auth/register/team", payload);
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const registerTrainer = async (payload) => {
        try {
            const { data } = await api.post("/auth/register/trainer", payload);
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const registerScout = async (payload) => {
        try {
            const { data } = await api.post("/auth/register/scout", payload);
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const updateSocials = async (socials) => {
        try {
            const { data } = await api.put("/users/me/socials", { socials });
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const updateIdentity = async (payload) => {
        try {
            const { data } = await api.put("/users/me/identity", payload);
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const updatePlayerProfile = async (payload) => {
        try {
            const { data } = await api.put("/players/me", payload);
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const addProfile = async (profile) => {
        try {
            const { data } = await api.post("/users/me/profiles", { profile });
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const removeProfile = async (profile) => {
        try {
            const { data } = await api.delete(`/users/me/profiles/${profile}`);
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const deletePlayerProfile = async () => {
        try {
            const { data } = await api.delete("/players/me");
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const switchProfile = async (profile) => {
        try {
            const { data } = await api.put("/users/me/active-profile", { profile });
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const downgradeToStandard = async () => {
        try {
            const { data } = await api.post("/users/me/downgrade");
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        setUser(null);
    };

    const toggleFavorite = async (teamId) => {
        if (!user) return { ok: false, error: "Please log in to save teams." };
        const isFav = user.favorites?.includes(teamId);
        try {
            if (isFav) {
                await api.delete(`/favorites/${teamId}`);
                setUser({ ...user, favorites: user.favorites.filter((id) => id !== teamId) });
            } else {
                await api.post(`/favorites/${teamId}`);
                setUser({ ...user, favorites: [...(user.favorites || []), teamId] });
            }
            return { ok: true, favorited: !isFav };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) };
        }
    };

    // Follow relationships: team/trainer/user. Persisted server-side.
    const [follows, setFollows] = useState({ team: [], trainer: [], user: [], event: [], trainer_listing: [] });

    const loadFollows = useCallback(async () => {
        if (!user) { setFollows({ team: [], trainer: [], user: [], event: [], trainer_listing: [] }); return; }
        try {
            const { data } = await api.get("/follows/me");
            setFollows(data);
        } catch { /* ignore */ }
    }, [user]);
    useEffect(() => { loadFollows(); }, [loadFollows]);

    const isFollowing = (targetType, targetId) =>
        !!(follows[targetType] && follows[targetType].includes(targetId));

    const toggleFollow = async (targetType, targetId) => {
        if (!user) return { ok: false, error: "Please log in to follow." };
        try {
            const { data } = await api.post(`/follows/${targetType}/${targetId}`);
            setFollows((prev) => {
                const list = prev[targetType] || [];
                const next = data.following
                    ? [...list, targetId]
                    : list.filter((id) => id !== targetId);
                return { ...prev, [targetType]: next };
            });
            return { ok: true, following: data.following, follower_count: data.follower_count };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) };
        }
    };

    const setFollowPrefs = async (targetType, targetId, prefs) => {
        try {
            const { data } = await api.put(`/follows/${targetType}/${targetId}/prefs`, { prefs });
            return { ok: true, prefs: data.prefs };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) };
        }
    };

    // ---- In-app notifications ---------------------------------------------
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const loadNotifications = useCallback(async () => {
        if (!user) { setNotifications([]); setUnreadCount(0); return; }
        try {
            const { data } = await api.get("/notifications");
            setNotifications(data.items || []);
            setUnreadCount(data.unread_count || 0);
        } catch { /* ignore */ }
    }, [user]);

    const refreshUnreadCount = useCallback(async () => {
        if (!user) { setUnreadCount(0); return; }
        try {
            const { data } = await api.get("/notifications/unread-count");
            setUnreadCount(data.unread_count || 0);
        } catch { /* ignore */ }
    }, [user]);

    useEffect(() => {
        if (!user) return undefined;
        refreshUnreadCount();
        const t = setInterval(refreshUnreadCount, 60000);
        return () => clearInterval(t);
    }, [user, refreshUnreadCount]);

    // ---- Direct-message unread count (drives header + Messages tab badges) ----
    const [unreadMessages, setUnreadMessages] = useState(0);

    const refreshMessagesUnread = useCallback(async () => {
        if (!user) { setUnreadMessages(0); return; }
        try {
            const { data } = await api.get("/messages/unread-count");
            setUnreadMessages(data.count || 0);
        } catch { /* ignore */ }
    }, [user]);

    useEffect(() => {
        if (!user) return undefined;
        refreshMessagesUnread();
        const t = setInterval(refreshMessagesUnread, 30000);
        return () => clearInterval(t);
    }, [user, refreshMessagesUnread]);

    // ---- New feed posts from followed accounts since the last feed visit ----
    const [feedNewCount, setFeedNewCount] = useState(0);

    const refreshFeedNewCount = useCallback(async () => {
        if (!user) { setFeedNewCount(0); return; }
        try {
            const { data } = await api.get("/feed/new-count");
            setFeedNewCount(data.count || 0);
        } catch { /* ignore */ }
    }, [user]);

    const markFeedSeen = useCallback(async () => {
        setFeedNewCount(0);
        try { await api.post("/feed/seen"); } catch { /* ignore */ }
    }, []);

    useEffect(() => {
        if (!user) return undefined;
        refreshFeedNewCount();
        const t = setInterval(refreshFeedNewCount, 60000);
        return () => clearInterval(t);
    }, [user, refreshFeedNewCount]);

    // ---- Blocked members (DMs only, mutual) -------------------------------
    const [blockedUsers, setBlockedUsers] = useState([]);

    const loadBlocks = useCallback(async () => {
        if (!user) { setBlockedUsers([]); return; }
        try {
            const { data } = await api.get("/messages/blocks");
            setBlockedUsers(data.blocked || []);
        } catch { /* ignore */ }
    }, [user]);
    useEffect(() => { loadBlocks(); }, [loadBlocks]);

    const isBlocked = (uid) => blockedUsers.includes(uid);

    const toggleBlock = async (uid) => {
        if (!user) return { ok: false, error: "Please log in." };
        const currently = blockedUsers.includes(uid);
        try {
            if (currently) {
                await api.delete(`/messages/block/${uid}`);
                setBlockedUsers((p) => p.filter((x) => x !== uid));
            } else {
                await api.post(`/messages/block/${uid}`);
                setBlockedUsers((p) => [...p, uid]);
            }
            return { ok: true, blocked: !currently };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) };
        }
    };

    const markNotificationRead = async (id) => {
        try {
            await api.post(`/notifications/${id}/read`);
            setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
            setUnreadCount((c) => Math.max(0, c - 1));
        } catch { /* ignore */ }
    };

    const markAllNotificationsRead = async () => {
        try {
            await api.post("/notifications/read-all");
            setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch { /* ignore */ }
    };

    const deleteNotification = async (id) => {
        try {
            await api.delete(`/notifications/${id}`);
            setNotifications((prev) => prev.filter((n) => n.id !== id));
        } catch { /* ignore */ }
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, googleSession, googleOAuthCallback, registerPlayer, registerTeam, registerTrainer, registerScout, updatePlayerProfile, updateSocials, updateIdentity, addProfile, removeProfile, deletePlayerProfile, switchProfile, downgradeToStandard, logout, toggleFavorite, refresh, follows, isFollowing, toggleFollow, setFollowPrefs, notifications, unreadCount, loadNotifications, refreshUnreadCount, markNotificationRead, markAllNotificationsRead, deleteNotification, unreadMessages, refreshMessagesUnread, feedNewCount, refreshFeedNewCount, markFeedSeen, feedBadgeTotal: (feedNewCount || 0) + (unreadMessages || 0), blockedUsers, isBlocked, toggleBlock }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
